import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    LayoutDashboard, Bed as BedIcon, User, 
    Calendar, Clock, ShieldCheck, MoreHorizontal,
    Search, Filter, ChevronLeft, Loader2, AlertCircle,
    UserPlus, X, ChevronRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SingleWardView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ward, setWard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdmitModal, setShowAdmitModal] = useState(false);
    const [selectedBed, setSelectedBed] = useState(null);
    const [patientSearch, setPatientSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchWard = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`http://localhost:5000/api/inpatient/wards/${id}`, { headers });
            setWard(response.data);
        } catch (err) {
            toast.error('Failed to load ward data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWard();
    }, [id]);

    const handlePatientSearch = async (val) => {
        setPatientSearch(val);
        if (val.length < 2) return setSearchResults([]);
        try {
            const res = await axios.get(`http://localhost:5000/api/patients?search=${val}`, { headers });
            setSearchResults(res.data);
        } catch (err) {}
    };

    const handleAdmit = async (patientId) => {
        try {
            await axios.post(`${window.location.protocol}//${window.location.hostname}/api/inpatient/admissions`, {
                patientId,
                bedId: selectedBed.id,
                admissionDate: new Date(),
                diagnosis: 'Routine monitoring'
            }, { headers });
            toast.success('Patient assigned to bed');
            setShowAdmitModal(false);
            fetchWard();
        } catch (err) {
            toast.error('Admission failed');
        }
    };

    const calculateAge = (dob) => {
        if (!dob) return 'N/A';
        const birthDate = new Date(dob);
        const diff = Date.now() - birthDate.getTime();
        return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    };

    const formatDateShort = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const calculateStay = (date) => {
        if (!date) return '0 days';
        const start = new Date(date);
        const now = new Date();
        const diff = now - start;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${days}d ${hours}h ${mins}m`;
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
        </div>
    );

    if (!ward) return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 space-y-4">
            <AlertCircle className="w-12 h-12 text-rose-500" />
            <p className="text-sm font-black text-slate-700 uppercase tracking-widest">Ward Not Found</p>
            <button onClick={() => navigate(-1)} className="px-6 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest">Go Back</button>
        </div>
    );

    return (
        <div className="p-4 md:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
            <div className="max-w-full mx-auto space-y-8">
                
                {/* Optimized Header */}
                <header className="flex justify-between items-center bg-white p-5 rounded-[24px] border border-slate-100 shadow-xl shadow-slate-200/5 overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500 opacity-50" />
                    <div className="flex items-center gap-4 relative z-10">
                        <button onClick={() => navigate(-1)} className="p-2.5 hover:bg-slate-50 border border-slate-50 rounded-xl transition-all">
                            <ChevronLeft className="w-4 h-4 text-slate-400" />
                        </button>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200">
                             <LayoutDashboard className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-lg font-black text-slate-800 tracking-tight uppercase leading-none">{ward.name} MONITOR</h1>
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1.5 leading-none">
                                {ward.category} UNIT • {ward.beds?.filter(b => b.isOccupied).length} PATIENTS ADMITTED
                            </p>
                        </div>
                    </div>
                </header>

                {/* Filters View Row like IMAGE */}
                <div className="flex justify-between items-center bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
                     <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300" />
                            <input placeholder="SCAN BED ID OR IDENTITY..." className="pl-10 pr-6 py-2.5 bg-slate-50 border border-slate-50 rounded-xl text-[9px] font-black outline-none focus:bg-white focus:border-indigo-100 transition-all w-[300px] uppercase tracking-wider" />
                        </div>
                        <button className="p-2.5 bg-slate-50 border border-slate-50 hover:border-slate-200 rounded-xl transition-all"><Filter className="w-3.5 h-3.5 text-slate-400" /></button>
                     </div>
                </div>

                {/* Dense Bed Detail Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                    {ward.beds?.sort((a,b) => a.bedNumber.localeCompare(b.bedNumber)).map(bed => {
                        const admission = bed.Admissions?.[0];
                        const patient = bed.Patient;
                        const doc = admission?.AdmittingDoctor;

                        return (
                            <div key={bed.id} className={`bg-white rounded-[32px] p-6 border transition-all hover:shadow-2xl hover:translate-y-[-2px] flex flex-col justify-between h-[450px] ${bed.isOccupied ? 'border-sky-100 shadow-sky-50' : 'border-slate-50 shadow-sm'}`}>
                                <div className="space-y-4">
                                    <div className="space-y-1 pt-1">
                                        <h3 className="text-[12px] font-black text-slate-800 tracking-tight uppercase leading-none">
                                           {ward.name} BED {bed.bedNumber}
                                        </h3>
                                        <p className="text-[9px] font-black text-indigo-500/50 uppercase tracking-widest">{ward.category} BED</p>
                                    </div>

                                    {bed.isOccupied ? (
                                        <div className="space-y-5 animate-in fade-in zoom-in-95 duration-500">
                                            {/* Patient Info Block */}
                                            <div className="space-y-1 py-1">
                                                <h4 className="text-[14px] font-black text-slate-800 leading-none uppercase tracking-tight">{patient?.firstName} {patient?.lastName}</h4>
                                                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mt-1">MRN: {patient?.medicalRecordNumber || `UM-${patient?.id}`}</p>
                                                <p className="text-[10px] font-bold text-slate-400 capitalize">
                                                    {patient?.gender}, {formatDateShort(patient?.dateOfBirth)} ({calculateAge(patient?.dateOfBirth)} yr)
                                                </p>
                                            </div>

                                            {/* Admitted Block */}
                                            <div className="space-y-3 pt-3 border-t border-slate-50">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">In Date</p>
                                                    <p className="text-[10px] font-bold text-slate-700 flex items-center gap-1.5 leading-none">
                                                        <Calendar className="w-3 h-3 text-indigo-400" /> {new Date(admission?.admissionDate).toLocaleDateString('en-GB')}
                                                    </p>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Stay</p>
                                                    <p className="text-[10px] font-bold text-slate-700 flex items-center gap-1.5 leading-none">
                                                        <Clock className="w-3 h-3 text-indigo-400" /> {calculateStay(admission?.admissionDate)}
                                                    </p>
                                                </div>
                                                <div className="pt-1">
                                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1">Doc In Charge</p>
                                                    <p className="text-[10px] font-black text-indigo-600 truncate uppercase tracking-tight">
                                                        {doc ? `DR. ${doc.firstName} ${doc.lastName}` : 'UNSPECIFIED'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Allergies Button */}
                                            <div className="pt-1">
                                                <button className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[9px] font-black uppercase tracking-widest border border-emerald-100 hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                                                    <AlertCircle className="w-3 h-3" /> Allergies
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex-grow flex flex-col justify-center items-center py-12 text-slate-300 bg-slate-50/30 rounded-[24px] border border-dashed border-slate-100">
                                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-3 shadow-sm">
                                                <BedIcon className="w-5 h-5 text-slate-200" />
                                            </div>
                                            <p className="text-[8px] font-black uppercase tracking-[0.2em]">Ready</p>
                                            <p className="text-[14px] font-black text-slate-400 mt-1 uppercase tracking-tighter">Vacant</p>
                                        </div>
                                    )}
                                </div>

                                {/* Actions Footer */}
                                <div className="pt-4 border-t border-slate-50 flex gap-2">
                                    <button className="p-3 bg-slate-50 hover:bg-slate-900 border border-slate-100 hover:text-white rounded-xl transition-all flex items-center justify-center">
                                        <MoreHorizontal className="w-4 h-4" />
                                    </button>
                                    <button 
                                        onClick={() => {
                                            if (bed.isOccupied) {
                                                // Handle actions
                                            } else {
                                                setSelectedBed(bed);
                                                setShowAdmitModal(true);
                                            }
                                        }}
                                        className={`flex-grow py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-md ${
                                            bed.isOccupied ? 'bg-indigo-600 hover:bg-black text-white' : 'bg-slate-100 hover:bg-indigo-600 hover:text-white text-slate-600'
                                        }`}
                                    >
                                        {bed.isOccupied ? 'Manage' : 'Assign'}
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Admission Picker Modal for Single Ward View */}
            {showAdmitModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4">
                     <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl p-10 animate-in zoom-in-95">
                         <div className="flex justify-between items-center mb-8">
                             <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-100"><UserPlus className="w-6 h-6" /></div>
                                <h2 className="text-xl font-black text-slate-800 tracking-tight">Assign to Bed {selectedBed.bedNumber}</h2>
                             </div>
                             <button onClick={() => setShowAdmitModal(false)} className="p-3 hover:bg-slate-50 rounded-2xl transition-all text-slate-300"><X /></button>
                         </div>
                         <div className="space-y-6">
                            <div className="relative">
                                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                <input 
                                    autoFocus
                                    placeholder="Search by name, ID or Email..."
                                    value={patientSearch}
                                    onChange={(e) => handlePatientSearch(e.target.value)}
                                    className="w-full h-16 bg-slate-50 border-2 border-slate-50 rounded-3xl pl-16 pr-8 text-sm font-black outline-none focus:bg-white focus:border-indigo-600 transition-all shadow-inner"
                                />
                            </div>
                            <div className="max-h-[300px] overflow-auto divide-y divide-slate-50">
                                {searchResults.map(p => (
                                    <div key={p.id} onClick={() => handleAdmit(p.id)} className="p-4 hover:bg-slate-50 cursor-pointer rounded-2xl transition-all flex justify-between items-center group">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-black text-[10px] text-slate-500 group-hover:bg-indigo-600 group-hover:text-white transition-all uppercase tracking-widest">P{p.id}</div>
                                            <div>
                                                <h4 className="text-sm font-black text-slate-800">{p.firstName} {p.lastName}</h4>
                                                <p className="text-[10px] font-bold text-slate-400">{p.email}</p>
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-200 group-hover:text-indigo-600 transition-all" />
                                    </div>
                                ))}
                            </div>
                         </div>
                     </div>
                </div>
            )}
        </div>
    );
};

const Home = ({className}) => <LayoutDashboard className={className} />;

export default SingleWardView;
